import { Logo, XMark } from "./Logo";
import { scrollToId } from "@/lib/scroll";
import { useLaunchModal } from "./LaunchModal";

const COLS = [
    {
        title: "Platform",
        tid: "footer-col-platform",
        links: [
            { l: "Markets", id: "markets" },
            { l: "Trade", id: "why-tavonx" },
            { l: "Earn", id: "tvx" },
            { l: "TNX", id: "tvx" },
        ],
    },
    {
        title: "Company",
        tid: "footer-col-company",
        links: [
            { l: "About", id: "global" },
            { l: "Ecosystem", id: "ecosystem" },
            { l: "Careers" },
            { l: "Contact" },
        ],
    },
    {
        title: "Legal",
        tid: "footer-col-legal",
        links: [{ l: "Terms of Service" }, { l: "Privacy Policy" }, { l: "Risk Disclosure" }],
    },
];

const SocialX = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" aria-hidden="true">
        <path d="M18.9 2H22l-6.8 7.8L23.2 22h-6.3l-4.9-6.4L6.4 22H3.3l7.3-8.3L1.5 2h6.4l4.4 5.9L18.9 2zm-1.1 18.1h1.7L7.1 3.8H5.3l12.5 16.3z" />
    </svg>
);

export default function Footer() {
    const { open } = useLaunchModal();
    /* Pre-launch: pages/socials that don't exist yet route into the launch modal
       instead of behaving as dead links. */
    const openSoon = (e) => {
        e.preventDefault();
        open("soon");
    };

    return (
        <footer id="about-footer" data-testid="footer" className="relative border-t border-white/[0.06] bg-void">
            <div className="mx-auto max-w-7xl px-5 pb-10 pt-20 sm:px-8 lg:px-12">
                <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-[1.6fr_1fr_1fr_1fr_1fr]">
                    <div data-testid="footer-col-brand">
                        <Logo sub="Crypto Exchange" onClick={scrollToId("hero")} />
                        <p className="mt-6 max-w-xs text-sm leading-relaxed text-slate-500">
                            A next-generation global cryptocurrency exchange built for speed,
                            security and a connected digital economy.
                        </p>
                    </div>

                    {COLS.map((c) => (
                        <div key={c.title} data-testid={c.tid}>
                            <h3 className="font-grotesk text-xs font-semibold uppercase tracking-[0.26em] text-white">
                                {c.title}
                            </h3>
                            <ul className="mt-6 space-y-3.5">
                                {c.links.map((k) => (
                                    <li key={k.l}>
                                        <a
                                            href={k.id ? `#${k.id}` : "#"}
                                            onClick={k.id ? scrollToId(k.id) : openSoon}
                                            className="relative text-sm text-slate-500 transition-colors duration-300 hover:text-cyber before:absolute before:-inset-x-3 before:-inset-y-2.5 before:content-['']"
                                        >
                                            {k.l}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}

                    <div data-testid="footer-col-social">
                        <h3 className="font-grotesk text-xs font-semibold uppercase tracking-[0.26em] text-white">
                            Social
                        </h3>
                        <div className="mt-6 flex items-center gap-3">
                            {[
                                { icon: <SocialX />, name: "X / Twitter", tid: "footer-social-x" },
                                { icon: <span className="text-[16px] leading-none">✈</span>, name: "Telegram", tid: "footer-social-telegram" },
                                { icon: <span className="font-grotesk text-xs font-bold">in</span>, name: "LinkedIn", tid: "footer-social-linkedin" },
                                { icon: <span className="font-grotesk text-xs font-bold">▶</span>, name: "YouTube", tid: "footer-social-youtube" },
                            ].map((s) => (
                                <a
                                    key={s.name}
                                    href="#"
                                    onClick={openSoon}
                                    data-testid={s.tid}
                                    aria-label={s.name}
                                    className="relative flex h-9 w-9 items-center justify-center rounded border border-white/10 text-slate-400 transition-all duration-300 hover:border-cyber/50 hover:text-cyber before:absolute before:-inset-1.5 before:content-['']"
                                >
                                    {s.icon}
                                </a>
                            ))}
                        </div>
                    </div>
                </div>

                <div
                    data-testid="footer-risk-disclosure"
                    className="mt-16 rounded-lg border border-white/[0.07] bg-panel/40 p-6"
                >
                    <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-cyber/80">
                        Risk Disclosure
                    </p>
                    <p className="mt-3 max-w-4xl text-xs leading-relaxed text-slate-500">
                        Trading cryptocurrencies and digital assets involves a high level of
                        risk, including the potential loss of all invested funds. Digital asset
                        markets can be volatile, and past performance is not indicative of
                        future results. Nothing on this website constitutes financial,
                        investment, legal or tax advice. Availability of products and services
                        may vary by jurisdiction. Please assess your personal circumstances
                        and seek independent advice where appropriate.
                    </p>
                </div>

                <div className="mt-10 flex flex-col items-center justify-between gap-4 border-t border-white/[0.06] pt-8 sm:flex-row">
                    <span data-testid="footer-copyright" className="font-mono text-[11px] tracking-[0.14em] text-slate-600">
                        © 2026 TAVONX. All rights reserved.
                    </span>
                    <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.28em] text-slate-600">
                        <XMark className="h-3.5 w-auto" withNode={false} />
                        Global · Fast · Secure · Liquid
                    </span>
                </div>
            </div>
        </footer>
    );
}
